import Emitter from "es6-event-emitter";

import { compact } from "./../../../common/utilities/utils";
import {
  countPercentOfContainerInZoom,
  getPercentInViewForSinglePage,
} from "./../../../common/utilities/percentageInViewUtils";

export default class HammerSlide extends Emitter {
  ratio = null;

  DOMContainer = null;
  DOMContent = null;
  swiperInstance = null;

  currentScale = 1;
  currentScrollLeft = 0;
  currentScrollTop = 0;

  cps = null;

  lastPinchTime = 0;

  doublePages = false;

  options = null;

  width = null;
  height = null;
  rightPagesArticlesIds = [];

  constructor(DOMContainer, DOMContent, pages, options, ratio) {
    super();
    this.DOMContainer = DOMContainer;
    this.DOMContent = DOMContent;
    this.ratio = ratio;
    this.pages = pages;
    this.doublePages = pages.length >= 2;
    this.options = options;
    this.viewportDimentionsData = {
      left: 0,
      top: 0,
      right: DOMContainer.clientWidth,
      bottom: DOMContainer.clientHeight,
      surfaceArea: DOMContainer.clientWidth * DOMContainer.clientHeight,
    };
    this._getRightPageArticles();
  }

  adaptMaxZoom() {
    if (
      document.querySelector("body").classList.contains("iOS") &&
      document.querySelector("body").classList.contains("Safari")
    ) {
      //pdf rendering, and safari and ios
      const basicArea = this.width * this.height;
      const maxArea = 16777246;
      const maxScale = Math.sqrt(maxArea / basicArea);
      this.options.max_zoom = Math.min(maxScale, this.options.max_zoom);
    }
  }

  isZoomed() {
    return this.currentScale > 1;
  }

  init(fullWidth) {
    this.setRatio();
    this.renderStyle(
      this.currentScale,
      this.currentScrollLeft,
      this.currentScrollTop
    );
    if (fullWidth) {
      this.zoomFullWidth();
    }
  }

  onPinchStart() {
    this.cps = this.currentScale;
  }

  onPinch(e) {
    //TODO: bug on double pages zoom
    // if no additionalEvent (pinchIn or pinchOut) it is a cancel event and should be rendered
    if (!e.additionalEvent) {
      return;
    }

    this.lastPinchTime = e.timeStamp;

    const lastScale = this.cps;
    const eScale = 1;
    this.cps = Math.min(
      this.options.max_zoom,
      Math.max(1, this.currentScale * e.scale * eScale)
    );
    if (this.cps <= 1) {
      this.cps = 1;
    }

    let resultX = this.currentScrollLeft + e.center.x;
    resultX *= this.cps / lastScale;
    resultX -= e.center.x;
    this.currentScrollLeft = resultX;

    let resultY = this.currentScrollTop + e.center.y;
    resultY *= this.cps / lastScale;
    resultY -= e.center.y;
    this.currentScrollTop = resultY;

    this.renderStyle(this.cps, this.currentScrollLeft, this.currentScrollTop);
  }

  onPinchEnd(e) {
    const eventData =
      this.cps !== this.currentScale ? this.getPercentageInView() : null;
    this.lastPinchTime = e.timeStamp;

    if (this.cps > this.currentScale) {
      this.trigger("hammerSlide:zoominend", eventData);
    } else if (this.cps < this.currentScale) {
      this.trigger("hammerSlide:zoomoutend", eventData);
    }
    this.currentScale = this.cps;
  }

  allowSwiping(e) {
    if (this.currentScale === 1) {
      return 1; //allow everything
    }
    let left = this.currentScrollLeft;
    if (e && e.deltaX) {
      left -= e.deltaX;
    }
    let max = this.DOMContent.offsetWidth - this.DOMContainer.offsetWidth - 1;
    if (max < 0) {
      max = 0;
    }
    if (left < 0) {
      left = 0;
    } else if (left > max) {
      left = max;
    }
    const scroll = left;
    if (scroll === 0 && scroll >= max) {
      return 1;
    } else if (scroll >= max) {
      return 2;
    } else if (scroll === 0) {
      return 3;
    }
    return 0;
  }

  panningEnd(e) {
    //don't take pan events into account right after pinching (jumping after zooming bug)
    if (
      e.timeStamp < this.lastPinchTime + this.options.pinchPanTimeInterval ||
      this.currentScale <= 1
    ) {
      return;
    }
    //// only on ios
    let left = this.currentScrollLeft;
    let top = this.currentScrollTop;
    left -= e.deltaX;
    top -= e.deltaY;

    const max = this.DOMContent.offsetWidth - this.DOMContainer.offsetWidth - 1;
    if (left < 0) {
      left = 0;
    } else if (left > max) {
      left = max;
    }

    this.currentScrollLeft = left;
    this.currentScrollTop = top;

    this.DOMContainer.scrollLeft = left;
    this.DOMContainer.scrollTop = top;

    this.trigger("hammerSlide:panend", this.getPercentageInView());
  }

  panning(e) {
    //don't take pan events into account right after pinching (jumping after zooming bug)
    if (
      e.timeStamp < this.lastPinchTime + this.options.pinchPanTimeInterval ||
      this.currentScale <= 1
    ) {
      return;
    }
    //// only on ios
    let left = this.currentScrollLeft;
    let top = this.currentScrollTop;
    left -= e.deltaX;
    top -= e.deltaY;

    const max = this.DOMContent.offsetWidth - this.DOMContainer.offsetWidth - 1;
    if (left < 0) {
      left = 0;
    } else if (left > max) {
      left = max;
    }

    this.DOMContainer.scrollLeft = left;
    this.DOMContainer.scrollTop = top;
  }

  // get x and y to put content in middle of container
  getBasicPan(width, height) {
    if (!this.slideRect) {
      this.slideRect = this.DOMContainer.getBoundingClientRect();
    }

    const w = this.slideRect.width - width;
    const h = this.slideRect.height - height;

    return {
      basicX: Math.max(0, w / 2),
      basicY: Math.max(0, h / 2),
    };
  }

  renderStyle(scale, offsetX, offsetY) {
    if (scale <= 1) {
      if (this.DOMContent.classList.contains("zoomed")) {
        this.DOMContent.classList.remove("zoomed");
        this.trigger("hammerSlide:notZoomed");
      }
    } else if (!this.DOMContent.classList.contains("zoomed")) {
      this.DOMContent.classList.add("zoomed");
      this.trigger("hammerSlide:zoomed");
    }

    if (!this.width && !this.height) {
      this.setWidthHeightRatio();
    }

    const finalWidth = this.width * scale;
    const finalHeight = this.height * scale;
    const finalTop = offsetY; // * scale;
    const finalLeft = offsetX; // * scale;

    const basic = this.getBasicPan(finalWidth, finalHeight);

    this.DOMContent.setAttribute(
      "style",
      `width:${finalWidth}px;height:${finalHeight}px;left:${basic.basicX}px;top:${basic.basicY}px;`
    );

    this.lastDOMContentRect = this.DOMContent.getBoundingClientRect();

    this.DOMContainer.scrollTop = finalTop;
    this.DOMContainer.scrollLeft = finalLeft;
  }

  setWidthHeightRatio() {
    let { width } = this.DOMContainer.getBoundingClientRect();
    const { height } = this.DOMContainer.getBoundingClientRect();

    if (this.doublePages) {
      width /= 2;
    }

    let finalWidth = height / this.ratio;
    let finalHeight = width * this.ratio;

    finalWidth = Math.min(finalWidth, width);
    if (this.doublePages) {
      finalWidth *= 2;
    }
    finalHeight = Math.min(finalHeight, height);

    this.width = finalWidth;
    this.height = finalHeight;

    this.adaptMaxZoom();
  }

  resize(height, keepScale, fullWidth) {
    const prevHeight = this.height;
    const prevWidth = this.width;
    const prevSlideRect = this.slideRect;

    this.DOMContainer.setAttribute("style", `height:${height}px`);

    if (fullWidth) {
      this.currentScale = 1;
      this.currentScrollLeft = 0;
      this.currentScrollTop = 0;
      this.setRatio();
      this.renderStyle(
        this.currentScale,
        this.currentScrollLeft,
        this.currentScrollTop
      );
      this.zoomFullWidth();
    } else {
      this.slideRect = this.DOMContainer.getBoundingClientRect();
      this.setWidthHeightRatio();
      if (!keepScale) {
        // dont keep scale
        this.currentScale = 1;
        this.currentScrollLeft = 0;
        this.currentScrollTop = 0;
      } else {
        this.renderStyle(1, 0, 0); //first render with no scale, otherwise swiper doesnt work anymore
        this.currentScrollLeft =
          ((this.currentScrollLeft + prevSlideRect.width / 2) * this.width) /
            prevWidth -
          this.slideRect.width / 2;
        this.currentScrollTop =
          ((this.currentScrollTop + prevSlideRect.height / 2) * this.height) /
            prevHeight -
          this.slideRect.height / 2;
        if (this.currentScrollTop < 0) {
          this.currentScrollTop = 0;
        }
        if (this.currentScrollLeft < 0) {
          this.currentScrollLeft = 0;
        }
      }
      this.renderStyle(
        this.currentScale,
        this.currentScrollLeft,
        this.currentScrollTop
      );
    }
    this.viewportDimentionsData = {
      left: 0,
      top: 0,
      right: this.DOMContainer.clientWidth,
      bottom: this.DOMContainer.clientHeight,
      surfaceArea:
        this.DOMContainer.clientWidth * this.DOMContainer.clientHeight,
    };
  }

  setRatio() {
    if (!this.width && !this.height) {
      this.setWidthHeightRatio();
    }
    const basic = this.getBasicPan(this.width, this.height);

    this.DOMContent.setAttribute(
      "style",
      `width:${this.width}px;height:${this.height}px;left:${basic.basicX}px;top:${basic.basicY}px;`
    );
  }

  zoomFullWidth() {
    const slideRect = this.DOMContainer.getBoundingClientRect();
    const domRect = this.DOMContent.getBoundingClientRect();
    const scale = slideRect.width / domRect.width;
    this.currentScale = scale;
    this.currentScrollLeft = 0;
    this.currentScrollTop = 0;

    this.renderStyle(
      this.currentScale,
      this.currentScrollLeft,
      this.currentScrollTop
    );
  }

  destroy() {
    //TODO: removeListeners
    this.ratio = null;
    this.DOMContainer = null;
    this.DOMContent = null;
    this.swiperInstance = null;
    this.currentScale = 1;
    this.currentScrollLeft = 0;
    this.currentScrollTop = 0;
    this.cps = null;
    this.lastPinchTime = 0;
    this.doublePages = false;
    this.options = null;
    this.width = null;
    this.height = null;
  }

  getPercentageInView() {
    if (
      this.DOMContainer.clientHeight < this.DOMContent.clientHeight ||
      this.DOMContainer.clientWidth < this.DOMContent.clientWidth
    ) {
      const pagesData = this._calculatePagesPercentageInView();
      const articlesData = this._calculateArticlesPercentageInView();

      return {
        ...pagesData,
        ...articlesData,
      };
    } else if (this._isEdgePageNotZoomed()) {
      // This is special case. When we are in doubled page mode
      // but user can see only one page (first or last page).
      // If the page is not zoomed, we return default values
      // for articles' percentageInView and 100% for the page.
      return this._countEdgePagePercentageInView();
    }
  }

  ////////////

  _calculateArticlesPercentageInView() {
    const articlesData = {};

    this.DOMContainer.querySelectorAll(".swiper-slide-page-click").forEach(
      (el) => {
        const xModifier =
          this.rightPagesArticlesIds.indexOf(el.dataset.contentid) !== -1
            ? this.DOMContent.clientWidth / 2
            : 0;

        const left = el.offsetLeft - this.DOMContainer.scrollLeft + xModifier;
        const top = el.offsetTop - this.DOMContainer.scrollTop;
        const right = left + el.clientWidth;
        const bottom = top + el.clientHeight;
        const articleCoordinates = {
          left: left,
          top: top,
          right: right,
          bottom: bottom,
        };

        articlesData[`article_${el.dataset.contentid}`] =
          countPercentOfContainerInZoom(
            this.viewportDimentionsData,
            articleCoordinates,
            this.options.activeAreaPercentage
          );
      }
    );

    return articlesData;
  }

  _calculatePagesPercentageInView() {
    if (this.doublePages) {
      const leftPageId = this.pages[0] ? this.pages[0].PublicationPageID : null;
      const rightPageId = this.pages[1]
        ? this.pages[1].PublicationPageID
        : null;

      const pageEl = this.DOMContainer.querySelector(
        `[data-page-id="${leftPageId || rightPageId}"]`
      );

      const pagePositionLeft = pageEl.offsetLeft;
      const pagePositionTop = pageEl.offsetTop;

      const pageCoordinates = {
        left: pagePositionLeft - this.DOMContainer.scrollLeft,
        top: pagePositionTop - this.DOMContainer.scrollTop,
        right:
          pagePositionLeft - this.DOMContainer.scrollLeft + pageEl.clientWidth,
        bottom:
          pagePositionTop - this.DOMContainer.scrollTop + pageEl.clientHeight,
      };

      const percentOfLeftPageInView = countPercentOfContainerInZoom(
        this.viewportDimentionsData,
        pageCoordinates
      );

      if (leftPageId && rightPageId) {
        return {
          [`page_${leftPageId}`]: percentOfLeftPageInView,
          [`page_${rightPageId}`]: Math.max(0, 100 - percentOfLeftPageInView),
        };
      }
      return {
        [`page_${leftPageId || rightPageId}`]: percentOfLeftPageInView,
      };
    }
  }

  _countEdgePagePercentageInView() {
    return compact(this.pages).map(getPercentInViewForSinglePage)[0];
  }

  _getRightPageArticles() {
    if (this.doublePages && this.pages[1]) {
      this.rightPagesArticlesIds = this.pages[1].content.reduce(
        (acc, article) => {
          acc.push(String(article.ContentID));
          return acc;
        },
        []
      );
    }
  }

  _isEdgePageNotZoomed() {
    return (
      (!this.DOMContainer.clientHeight < this.DOMContent.clientHeight ||
        !this.DOMContainer.clientWidth < this.DOMContent.clientWidth) &&
      this.doublePages &&
      compact(this.pages).length !== 2
    );
  }
}



// WEBPACK FOOTER //
// ./src/touch/reader/components/hammerSlide.js